import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { BaseSchema } from 'yup';

import './_yup.extensions';

import { isPostalCodeValid, validateDateOfBirth } from 'services/validation';
import { DATE_VALID } from 'settings/birthdayValidValues';
import {
  FIELD_COUNTRY_MAX_FR,
  FIELD_COUNTRY_MAX_UK,
} from 'settings/constraints';
import {
  CELLPHONE_FR_LENGTH_REGEX,
  CELLPHONE_FR_REGEX,
  CELLPHONE_UK_LENGTH_REGEX,
  CELLPHONE_UK_REGEX,
  EMAIL_REGEX,
  FIRSTNAME_REGEX,
  LANDLINE_FR_LENGTH_REGEX,
  LANDLINE_FR_REGEX,
  LANDLINE_UK_LENGTH_REGEX,
  LANDLINE_UK_REGEX,
  LASTNAME_REGEX,
  PASSWORD_REGEX,
  POSTCODE_UK_REGEX,
} from 'settings/regex';

export default (isUk: boolean, t: TFunction): BaseSchema =>
  Yup.object().shape({
    email: Yup.string()
      .required(t('LABEL_ERROR_INVALID_EMAIL'))
      .email(t('LABEL_ERROR_INVALID_EMAIL', { ns: 'signUpPage' }))
      .test(
        'email',
        t('LABEL_ERROR_INVALID_EMAIL', { ns: 'signUpPage' }),
        function (value) {
          if (value !== undefined) {
            const isEmail = EMAIL_REGEX.test(value);
            const isNumeric = /^\d{8}$/.test(value);
            return isEmail || isNumeric;
          }
          return false;
        }
      )
      .matches(/^[^&]*$/, t('LABEL_ERROR_CARAC')),
    confirmEmail: Yup.string()
      .required(t('LABEL_ERROR_CONFIRM_EMAIL_REQUIRED'))
      .oneOf([Yup.ref('email'), null], t('LABEL_ERROR_CONFIRM_EMAIL_NOT_SAME')),
    password: Yup.string()
      .required(' ')
      .max(32, t('LABEL_ERROR_PASSWORD_MAX'))
      .matches(PASSWORD_REGEX, ' '),
    confirmPassword: Yup.string()
      .required(
        t('LABEL_ERROR_CONFIRM_PASSWORD_NOT_SAME', { ns: 'translation' })
      )
      .oneOf(
        [Yup.ref('password'), null],
        t('LABEL_ERROR_CONFIRM_PASSWORD_NOT_SAME', { ns: 'translation' })
      ),
    civility: Yup.string().required(t('LABEL_ERROR_TITLE_REQUIRED')),
    surname: Yup.string()
      .required(t('LABEL_ERROR_SURNAME_REQUIRED'))
      .min(2, t('LABEL_ERROR_SURNAME_MAX'))
      .max(isUk ? 32 : 38, t('LABEL_ERROR_SURNAME_MAX'))
      .matches(LASTNAME_REGEX, t('LABEL_ERROR_SURNAME_MUST_LETTERS')),
    firstName: Yup.string()
      .required(t('LABEL_ERROR_FIRSTNAME_REQUIRED'))
      .min(2, t('LABEL_ERROR_FIRST_NAME_LENGTH'))
      .max(isUk ? 32 : 38, t('LABEL_ERROR_FIRST_NAME_LENGTH'))
      .matches(FIRSTNAME_REGEX, t('LABEL_ERROR_FIRST_NAME_MUST_LETTERS')),
    nameWay: Yup.string()
      .required(t('LABEL_ERROR_NAME_WAY_REQUIRED'))
      .min(2, t('LABEL_ERROR_NAME_WAY_LENGTH'))
      .max(isUk ? 32 : 38, t('LABEL_ERROR_NAME_WAY_LENGTH'))
      .matches(/^[^&]*$/, {
        excludeEmptyString: true,
        message: t('LABEL_ERROR_CARAC', { ns: 'translation' }),
      }),
    nameComplement: Yup.string()
      .min(2, t('LABEL_ERROR_NAME_COMPLEMENT_LENGTH'))
      .max(isUk ? 32 : 38, t('LABEL_ERROR_NAME_COMPLEMENT_LENGTH'))
      .matches(/^[^&]*$/, {
        excludeEmptyString: true,
        message: t('LABEL_ERROR_CARAC', { ns: 'translation' }),
      }),
    nameAddress: Yup.string()
      .min(2, t('LABEL_ERROR_NAME_ADDRESS_LENGTH'))
      .max(isUk ? 32 : 38, t('LABEL_ERROR_NAME_ADDRESS_LENGTH'))
      .matches(/^[^&]*$/, {
        excludeEmptyString: true,
        message: t('LABEL_ERROR_CARAC', { ns: 'translation' }),
      }),
    locality: Yup.string()
      .min(2, t('LABEL_ERROR_NAME_SAID_PLACE_LENGTH'))
      .max(isUk ? 32 : 38, t('LABEL_ERROR_NAME_SAID_PLACE_LENGTH'))
      .matches(/^[^&]*$/, {
        excludeEmptyString: true,
        message: t('LABEL_ERROR_CARAC', { ns: 'translation' }),
      }),
    postCode: Yup.string()
      .required(t('LABEL_ERROR_POSTCODE_REQUIRED'))
      .min(isUk ? 6 : 5, t('LABEL_ERROR_POSTCODE_LENGTH'))
      .max(isUk ? 8 : 5, t('LABEL_ERROR_POSTCODE_LENGTH'))
      .test('postcode', t('LABEL_ERROR_POSTCODE_REGEX'), function (value) {
        return !value
          ? true
          : isUk
          ? POSTCODE_UK_REGEX.test(value)
          : isPostalCodeValid(value);
      }),
    city: Yup.string()
      .required(t('LABEL_ERROR_CITY_REQUIRED'))
      .max(
        isUk ? FIELD_COUNTRY_MAX_UK : FIELD_COUNTRY_MAX_FR,
        t('LABEL_ERROR_CITY_MAX', { ns: 'translation' })
      )
      .matches(/^[^&]*$/, {
        excludeEmptyString: true,
        message: t('LABEL_ERROR_CARAC', { ns: 'translation' }),
      }),
    landLine: Yup.string()
      .matches(
        isUk ? LANDLINE_UK_LENGTH_REGEX : LANDLINE_FR_LENGTH_REGEX,
        t('LABEL_ERROR_PHONE_NUMBER_LENGTH', { ns: 'signUpPage' })
      )
      .matches(
        isUk ? LANDLINE_UK_REGEX : LANDLINE_FR_REGEX,
        t('LABEL_ERROR_PHONE_NUMBER_REGEX', { ns: 'signUpPage' })
      ),
    mobileNumber: Yup.string()
      .matches(
        isUk ? CELLPHONE_UK_LENGTH_REGEX : CELLPHONE_FR_LENGTH_REGEX,
        t('LABEL_ERROR_CELLPHONE_NUMBER_LENGTH', { ns: 'signUpPage' })
      )
      .matches(
        isUk ? CELLPHONE_UK_REGEX : CELLPHONE_FR_REGEX,
        t('LABEL_ERROR_CELLPHONE_NUMBER_REGEX', { ns: 'signUpPage' })
      )
      .when('newsAfibelSms', {
        is: 'O',
        then: Yup.string().required(
          t('LABEL_ERROR_CELLPHONE_REQUIRED', { ns: 'signUpPage' })
        ),
      }),
    dateOfBirth: Yup.string().test(
      'dateOfBirth',
      t('LABEL_BIRTHDAY_INVALID', { ns: 'translation' }),
      function (value) {
        if (!value) return true;

        const [day, month, year] = value.split('/');
        const validState = validateDateOfBirth(
          parseInt(month),
          parseInt(day),
          parseInt(year)
        );

        return validState === DATE_VALID;
      }
    ),
    newsAfibelMail: Yup.string().required(t('LABEL_ERROR_NEWSLETTER')),
    newsAfibelSms: Yup.string()
      .test(
        'newsAfibelSms',
        t('LABEL_ERROR_AFIBEL_SMS_NEWSLETTER'),
        function (value: string | undefined) {
          const { mobileNumber } = this.parent;

          return !mobileNumber?.trim() || !!value?.trim();
        }
      )
      .test(
        'Cellphone required',
        t('LABEL_ERROR_SMS_CELLPHONE_REQUIRED'),
        function (value: string | undefined): boolean {
          const { mobileNumber } = this.parent;

          return !(value === 'O' && !mobileNumber?.trim());
        }
      ),
    newsPartnerAfibelMail: Yup.string().required(t('LABEL_ERROR_NEWSLETTER')),
    turnstileToken: Yup.string().turnstileRequired(
      t('FORM_TURNSTILE_REQUIRED_ERROR', { ns: 'translation' })
    ),
  });
